import LoadingSpinner from "@/components/LoadingSpinner";
import useRules from "@/hooks/useRules";
import * as React from "react";
import RuleRowItem from "./RuleRowItem";
import { useState } from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import RuleEditModal from "./RuleEditModal";
import QuickRuleSetupModal from "./QuickRuleSetupModal";
import RuleVersionHistoryModal from "./RuleVersionHistoryModal";
import { Rule } from "./types";
import IconChevronDown from "@/icons/IconChevronDown";
import IconChevronUp from "@/icons/IconChevronUp";

export default function RuleManager() {
  const { rules: workflowRules, loading } = useRules();

  const [workingRuleIndex, setWorkingRuleIndex] = useState<number | null | undefined>(undefined);
  const [manualPreset, setManualPreset] = useState<Partial<Rule> | undefined>(undefined);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [historyRule, setHistoryRule] = useState<Rule | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  if (loading) {
    return <LoadingSpinner />;
  }

  function renderModal() {
    if (showQuickSetup) {
      return (
        <QuickRuleSetupModal
          close={() => setShowQuickSetup(false)}
          openManualEditor={(preset) => {
            setShowQuickSetup(false);
            setManualPreset(preset);
            setWorkingRuleIndex(null);
          }}
        />
      );
    }

    if (workingRuleIndex !== undefined) {
      const rule =
        workingRuleIndex === null
          ? manualPreset
          : workflowRules[workingRuleIndex];
      return (
        <RuleEditModal
          rule={rule}
          close={() => {
            setWorkingRuleIndex(undefined);
            setManualPreset(undefined);
          }}
        />
      );
    }

    return null;
  }

  return (
    <>
      {/* How Rules Work info panel */}
      <div className="mt-4 mb-2 border border-slate-200 rounded-lg overflow-hidden max-w-2xl">
        <button
          type="button"
          onClick={() => setShowHowItWorks(!showHowItWorks)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700 text-left transition-colors"
        >
          <span>ℹ️ How do rules work?</span>
          {showHowItWorks
            ? <IconChevronUp width={15} height={15} className="text-slate-400" />
            : <IconChevronDown width={15} height={15} className="text-slate-400" />}
        </button>
        {showHowItWorks && (
          <div className="px-4 py-3 text-sm text-slate-600 bg-white border-t border-slate-200 space-y-2">
            <p>
              Rules run in <strong>priority order</strong> from lowest number to highest (1 runs first).
              The first rule whose conditions match is applied — later rules are skipped for that trigger type.
            </p>
            <p>
              <strong>No conditions = always matches.</strong> A rule with no conditions acts as a
              catch-all fallback. Put it last (highest priority number) so more specific rules run first.
            </p>
            <p>
              <strong>Multiple rules for the same trigger</strong> let you handle different scenarios:
              for example, route to Sales when a visitor selects "Sales" in the prechat survey,
              and route to Support otherwise.
            </p>
            <p>
              <strong>Conditions:</strong> Use <em>Attribute</em> conditions to key off survey answers,
              URL, time of day, visitor language, and more. Conditions within a group use AND logic;
              multiple groups use OR logic.
            </p>
            <p>
              Use <strong>⚡ Quick Setup</strong> to create common routing rules in a guided wizard,
              or <strong>New Rule</strong> to build a rule manually.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-2">
        <SecondaryBlueButton
          onClick={() => setShowQuickSetup(true)}
          label="⚡ Quick Setup"
        />
        <PrimaryBlueButton
          onClick={() => {
            setManualPreset(undefined);
            setWorkingRuleIndex(null);
          }}
          label="New Rule"
        />
      </div>
      <div className="flex pt-10 pl-10 pr-5">
        <div className="flex-1 mr-5 h-30">
          {workflowRules.map((r, i) => (
            <RuleRowItem
              editRule={() => {
                setManualPreset(undefined);
                setWorkingRuleIndex(i);
              }}
              showHistory={() => setHistoryRule(r)}
              key={i}
              rule={r}
            />
          ))}
        </div>
      </div>
      {renderModal()}
      {historyRule && (
        <RuleVersionHistoryModal
          rule={historyRule}
          close={() => setHistoryRule(null)}
          onRestore={(restored) => {
            setHistoryRule(null);
            setManualPreset(restored);
            setWorkingRuleIndex(null);
          }}
        />
      )}
    </>
  );
}
